import React, { useMemo, useState } from 'react';
import { MONTH_LABELS } from './config/trackers';
import InlineDatePicker from '../global/InlineDatePicker';
import {
    parseCell,
    serializeCell,
    parsePaymentMethod,
    formatMoney,
    todayIso,
    getCellTotalSales,
    isCellFullyVerified,
    cellsForYear,
} from './utils/cells';

/**
 * Per-cell bottom-sheet editor.
 *
 * target = { row, slot ('months'|'subTaxes'), index }; `year` selects which
 * year's grid to edit. Reads the encoded cell string for that year via
 * cellsForYear, lets the user add/verify/remove payments, and emits a fresh
 * serialized string back through onSave(cellValue) — the parent persists it
 * against the same year.
 *
 * Tracker-specific behaviors:
 *   - salesTax  → dual-field (Tax Paid + Sales Reported), blue gradient summary.
 *   - tenantRent → late-fee row inside summary; no auto-pay banner (incoming).
 *   - Auto-pay rows show the blue confirm banner with quick-confirm.
 *   - Fully paid → green saved banner replaces the destructive footer.
 */
function PaymentPopup({ tracker, target, year, onClose, onSave }) {
    const { row, slot, index } = target;
    const displayYear = year || new Date().getFullYear();
    const initialParsed = useMemo(
        () => parseCell(cellsForYear(row, displayYear, slot)[index]),
        [row, slot, index, displayYear],
    );

    const [parsed, setParsed] = useState(initialParsed);
    const [draftAmt, setDraftAmt] = useState('');
    const [draftSales, setDraftSales] = useState('');
    const [draftDate, setDraftDate] = useState(todayIso());
    const [draftDueDate, setDraftDueDate] = useState('');
    const [error, setError] = useState('');
    const [showConfirmClear, setShowConfirmClear] = useState(false);

    const method = parsePaymentMethod(row.paymentMethod);
    const isSalesTax = tracker.key === 'salesTax';
    const isTenantRent = tracker.key === 'tenantRent';
    const isAnnual = slot === 'subTaxes';

    const cellLabel = isAnnual
        ? subTaxLabel(tracker, row, index)
        : `${MONTH_LABELS[index]} ${displayYear}`;
    const expected = parsed.expected || estimateExpected(row, tracker);
    const totalPaid = (parsed.payments || []).reduce(
        (s, p) => s + (Number(p.amt) || 0),
        0,
    );
    const balance = Math.max(0, expected - totalPaid);
    const totalSales = getCellTotalSales(parsed);
    const calcRate = totalSales > 0
        ? `${((totalPaid / totalSales) * 100).toFixed(2)}%`
        : '—';

    const fullyPaid = totalPaid > 0 && totalPaid >= expected;
    const allVerified = isCellFullyVerified(parsed);

    const lateFee = computeLateFee(row, parsed, draftDate);

    // Auto-pay banner state
    let autoBannerKind = null;
    if (!isTenantRent && method.isAuto) {
        if ((parsed.payments || []).length === 0) {
            autoBannerKind = 'normal'; // blue
        } else if (!allVerified) {
            autoBannerKind = 'warning'; // red
        }
    }

    const handleAdd = (verifiedNow) => {
        const amt = Number(String(draftAmt).replace(/[^\d.]/g, ''));
        if (!amt || amt <= 0) {
            setError('Enter a payment amount greater than 0.');
            return;
        }
        if (!draftDate) {
            setError('Select a payment date.');
            return;
        }
        const sales = isSalesTax ? Number(String(draftSales).replace(/[^\d.]/g, '')) : 0;
        if (isSalesTax && (!sales || sales <= 0)) {
            setError('Enter the total sales reported for this filing.');
            return;
        }
        setError('');
        const nextPayments = [
            ...(parsed.payments || []),
            {
                amt,
                date: draftDate,
                dueDate: draftDueDate || '',
                verified: !!verifiedNow,
                ...(isSalesTax ? { sales } : {}),
            },
        ];
        const nextExpected = expected || amt;
        const next = {
            type: nextPayments.length > 1 ? 'multi' : 'paid',
            expected: nextExpected,
            payments: nextPayments,
        };
        setParsed(next);
        setDraftAmt('');
        setDraftSales('');
        setDraftDueDate('');
    };

    const removePayment = (idx) => {
        const next = (parsed.payments || []).filter((_, i) => i !== idx);
        if (next.length === 0) {
            setParsed({ type: 'future', expected: 0, payments: [] });
        } else {
            setParsed({
                type: next.length > 1 ? 'multi' : 'paid',
                expected: parsed.expected,
                payments: next,
            });
        }
    };

    const verifyPayment = (idx) => {
        const next = (parsed.payments || []).map((p, i) =>
            i === idx ? { ...p, verified: true } : p,
        );
        setParsed({ ...parsed, payments: next });
    };

    const clearAll = () => {
        setParsed({ type: 'future', expected: 0, payments: [] });
        setShowConfirmClear(false);
    };

    const handleSave = () => {
        onSave(serializeCell(parsed));
    };

    const handleQuickConfirm = () => {
        const amt = Number(String(draftAmt).replace(/[^\d.]/g, '')) || expected;
        if (!amt) {
            setError('Enter the auto-debited amount.');
            return;
        }
        setError('');
        const debitDate = method.day
            ? deriveAutoPayDate(draftDate, method.day)
            : draftDate;
        const nextPayments = [
            ...(parsed.payments || []),
            { amt, date: debitDate, dueDate: draftDueDate || '', verified: true },
        ];
        setParsed({
            type: nextPayments.length > 1 ? 'multi' : 'paid',
            expected: expected || amt,
            payments: nextPayments,
        });
        setDraftAmt('');
    };

    const subText = fullyPaid
        ? '✓ Paid in full'
        : (totalPaid > 0
            ? `Partial · ${formatMoney(totalPaid)} of ${formatMoney(expected)}`
            : (expected > 0 ? `Expected ${formatMoney(expected)}` : 'No expected amount yet'));

    return (
        <div className="bt-overlay popup" onClick={onClose}>
            <div
                className="bt-pp"
                role="dialog"
                aria-modal="true"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="bt-pp-drag-handle" />
                <button
                    className="bt-pp-close-btn"
                    onClick={onClose}
                    aria-label="Close"
                    type="button"
                >
                    ✕
                </button>
                <div className="bt-pp-scroll">
                    {showConfirmClear ? (
                        <ConfirmClearPanel
                            count={(parsed.payments || []).length}
                            onCancel={() => setShowConfirmClear(false)}
                            onConfirm={clearAll}
                        />
                    ) : (
                        <>
                            <div className="bt-pp-title">{cellLabel} — {row.label}</div>
                            <div className={`bt-pp-sub${fullyPaid ? ' paid' : ''}`}>
                                {subText}
                            </div>

                            {/* Auto-pay banner */}
                            {autoBannerKind === 'normal' && (
                                <div className="bt-pp-auto-banner">
                                    <div className="bt-pp-auto-icon"><span role="img" aria-label="Auto-pay">🔄</span></div>
                                    <div className="bt-pp-auto-text">
                                        <div className="bt-pp-auto-title">
                                            {method.method} · day {method.day}
                                        </div>
                                        <div className="bt-pp-auto-sub">
                                            Always verify in your bank that the debit went through.
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className="bt-pp-auto-confirm"
                                        onClick={handleQuickConfirm}
                                    >
                                        <span role="img" aria-label="Confirm verified">✓✓</span>{' '}
                                        I checked the bank — Confirm{' '}
                                        {formatMoney(Number(String(draftAmt).replace(/[^\d.]/g, '')) || expected)}{' '}
                                        on {formatDateShort(method.day ? deriveAutoPayDate(draftDate, method.day) : draftDate)}
                                    </button>
                                </div>
                            )}
                            {autoBannerKind === 'warning' && (
                                <div className="bt-pp-auto-banner warning">
                                    <div className="bt-pp-auto-icon"><span role="img" aria-label="Warning">⚠️</span></div>
                                    <div className="bt-pp-auto-text">
                                        <div className="bt-pp-auto-title">
                                            Recorded but not verified
                                        </div>
                                        <div className="bt-pp-auto-sub">
                                            Confirm the debit landed in your bank, then mark each payment ✓ Verify below.
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Sales-tax blue gradient summary */}
                            {isSalesTax && (parsed.payments || []).length > 0 && (
                                <div className="bt-pp-tax-summary">
                                    <div className="bt-pp-tax-summary-row">
                                        <div className="bt-pp-tax-stat">
                                            <div className="bt-pp-tax-stat-label">Sales Reported</div>
                                            <div className="bt-pp-tax-stat-value">
                                                {formatMoney(totalSales)}
                                            </div>
                                        </div>
                                        <div className="bt-pp-tax-stat">
                                            <div className="bt-pp-tax-stat-label">Tax Paid</div>
                                            <div className="bt-pp-tax-stat-value">
                                                {formatMoney(totalPaid)}
                                            </div>
                                        </div>
                                        <div className="bt-pp-tax-stat">
                                            <div className="bt-pp-tax-stat-label">Calc. Rate</div>
                                            <div className="bt-pp-tax-stat-value">{calcRate}</div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Standard expected/paid summary */}
                            {expected > 0 && (
                                <div className="bt-pp-summary">
                                    <div className="bt-pp-summary-row">
                                        <span>Expected</span>
                                        <strong>{formatMoney(expected)}</strong>
                                    </div>
                                    <div className="bt-pp-summary-row">
                                        <span>Paid</span>
                                        <strong style={{ color: 'var(--bt-green-text)' }}>
                                            {formatMoney(totalPaid)}
                                        </strong>
                                    </div>
                                    {isTenantRent && lateFee > 0 && (
                                        <div className="bt-pp-late-fee-row">
                                            <span>
                                                <span role="img" aria-label="Late fee">⚠️</span>{' '}
                                                Late fee (after day {row.fields.lateFeeAfterDay})
                                            </span>
                                            <strong>+{formatMoney(lateFee)}</strong>
                                        </div>
                                    )}
                                    <div className="bt-pp-summary-row balance">
                                        <span>{fullyPaid ? 'Fully paid' : 'Balance'}</span>
                                        <strong
                                            style={{
                                                color: fullyPaid ? 'var(--bt-green-text)' : 'var(--bt-red)',
                                            }}
                                        >
                                            {formatMoney(fullyPaid ? totalPaid : balance)}
                                        </strong>
                                    </div>
                                </div>
                            )}

                            {/* Existing payment list */}
                            {(parsed.payments || []).length > 0 && (
                                <>
                                    <div className="bt-pp-list-label">Payments received</div>
                                    {(parsed.payments || []).map((p, i) => (
                                        <div
                                            key={i}
                                            className={`bt-pp-pay-row${p.verified ? ' verified' : ' unverified'}`}
                                        >
                                            <div className="bt-pp-pay-info">
                                                <div className="bt-pp-pay-amt">{formatMoney(p.amt)}</div>
                                                <div className="bt-pp-pay-date">
                                                    {formatDateLong(p.date)}
                                                </div>
                                                {p.dueDate && (
                                                    <div className="bt-pp-pay-date">
                                                        Due: {formatDateLong(p.dueDate)}
                                                    </div>
                                                )}
                                                {isSalesTax && p.sales > 0 && (
                                                    <div className="bt-pp-pay-sales">
                                                        <span role="img" aria-label="Sales reported">📊</span>{' '}
                                                        {formatMoney(p.sales)} sales
                                                    </div>
                                                )}
                                                {p.verified ? (
                                                    <div className="bt-pp-pay-verified">
                                                        <span role="img" aria-label="Verified">✓✓</span> Verified
                                                    </div>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="bt-pp-pay-verify-btn"
                                                        onClick={() => verifyPayment(i)}
                                                    >
                                                        ✓ Verify
                                                    </button>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                className="bt-pp-pay-del"
                                                onClick={() => removePayment(i)}
                                                aria-label="Remove payment"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    ))}
                                </>
                            )}

                            {/* Saved success banner — only when fully paid AND verified */}
                            {fullyPaid && allVerified && (
                                <div className="bt-pp-saved-banner">
                                    <span className="bt-pp-saved-icon" role="img" aria-label="Saved">✓</span>
                                    <div>
                                        <div className="bt-pp-saved-title">Saved · Paid in full</div>
                                        <div className="bt-pp-saved-sub">
                                            All {(parsed.payments || []).length} payment{(parsed.payments || []).length === 1 ? '' : 's'} recorded successfully
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Add payment form (collapses when fully paid) */}
                            {!fullyPaid && (
                                <div className="bt-pp-add-section">
                                    <div className="bt-pp-list-label">
                                        {(parsed.payments || []).length > 0 ? 'Add another payment' : 'Add payment'}
                                    </div>
                                    {isSalesTax ? (
                                        <>
                                            <div className="bt-field">
                                                <label className="bt-field-label">Total Sales Reported *</label>
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    className="bt-field-input"
                                                    placeholder="e.g. $21,500"
                                                    value={draftSales}
                                                    onChange={(e) => setDraftSales(e.target.value)}
                                                />
                                            </div>
                                            <div className="bt-field">
                                                <label className="bt-field-label">Tax Paid *</label>
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    className="bt-field-input"
                                                    placeholder="e.g. $1,344"
                                                    value={draftAmt}
                                                    onChange={(e) => setDraftAmt(e.target.value)}
                                                />
                                            </div>
                                            <div className="bt-field">
                                                <label className="bt-field-label">Due Date</label>
                                                <InlineDatePicker
                                                    value={draftDueDate}
                                                    onChange={setDraftDueDate}
                                                    placeholder="Due date"
                                                />
                                            </div>
                                            <div className="bt-field">
                                                <label className="bt-field-label">Filing Date *</label>
                                                <InlineDatePicker
                                                    value={draftDate}
                                                    onChange={setDraftDate}
                                                    placeholder="Filing date"
                                                />
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="bt-field">
                                                <label className="bt-field-label">Amount *</label>
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    className="bt-field-input"
                                                    placeholder={expected > 0 ? formatMoney(expected) : '$0'}
                                                    value={draftAmt}
                                                    onChange={(e) => setDraftAmt(e.target.value)}
                                                />
                                            </div>
                                            <div className="bt-field">
                                                <label className="bt-field-label">Due Date</label>
                                                <InlineDatePicker
                                                    value={draftDueDate}
                                                    onChange={setDraftDueDate}
                                                    placeholder="Due date"
                                                />
                                            </div>
                                            <div className="bt-field">
                                                <label className="bt-field-label">Payment Date *</label>
                                                <InlineDatePicker
                                                    value={draftDate}
                                                    onChange={setDraftDate}
                                                    placeholder="Payment date"
                                                />
                                            </div>
                                        </>
                                    )}
                                    {error && <div className="bt-field-error">{error}</div>}
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        <button
                                            type="button"
                                            className="bt-btn bt-btn-save"
                                            onClick={() => handleAdd(false)}
                                            style={{ flex: 1 }}
                                        >
                                            + Add Payment
                                        </button>
                                        <button
                                            type="button"
                                            className="bt-btn bt-btn-ghost"
                                            onClick={() => handleAdd(true)}
                                            style={{ flex: 1 }}
                                        >
                                            + Add &amp; Verify
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Action footer: Close + (Clear All when payments exist and not fully verified-paid) */}
                            <div className="bt-modal-actions">
                                <button
                                    type="button"
                                    className="bt-btn bt-btn-ghost"
                                    onClick={handleSave}
                                >
                                    {fullyPaid && allVerified ? 'Done' : 'Save & Close'}
                                </button>
                                {(parsed.payments || []).length > 0 && (
                                    <button
                                        type="button"
                                        className="bt-btn bt-btn-danger"
                                        onClick={() => setShowConfirmClear(true)}
                                    >
                                        Clear All
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

/** Inline destructive confirmation panel — replaces the popup body. */
function ConfirmClearPanel({ count, onCancel, onConfirm }) {
    return (
        <div className="bt-pp-confirm-wrap">
            <div className="bt-pp-confirm-icon"><span role="img" aria-label="Warning">⚠️</span></div>
            <div className="bt-pp-confirm-title">Clear all payments?</div>
            <div className="bt-pp-confirm-text">
                This will permanently remove the <strong>{count}</strong> payment{count === 1 ? '' : 's'} on this cell. This action cannot be undone — the cell will reset to upcoming.
            </div>
            <div className="bt-modal-actions">
                <button type="button" className="bt-btn bt-btn-ghost" onClick={onCancel}>
                    No, keep them
                </button>
                <button type="button" className="bt-btn bt-btn-danger" onClick={onConfirm}>
                    Yes, clear all
                </button>
            </div>
        </div>
    );
}

function subTaxLabel(tracker, row, index) {
    return row.subTaxLabels?.[index]
        || tracker.subTaxLabels?.[index]
        || `Slot ${index + 1}`;
}

function estimateExpected(row, tracker) {
    const f = row.fields || {};
    if (tracker.key === 'tenantRent') {
        return Number(String(f.monthlyRent || '').replace(/[^\d.]/g, '')) || 0;
    }
    if (tracker.key === 'mortgage') {
        return Number(String(f.monthlyPayment || '').replace(/[^\d.]/g, '')) || 0;
    }
    const candidates = [
        f.monthlyCost, f.monthlyPayment, f.monthlyRent,
        f.costPerService, f.totalPremium, f.premiumAmount,
    ];
    for (const c of candidates) {
        const n = Number(String(c || '').replace(/[^\d.]/g, ''));
        if (n > 0) return n;
    }
    return 0;
}

/**
 * Late-fee surfacing for tenantRent: only when policy is configured AND no
 * payment is recorded yet AND draft date crosses the configured day.
 */
function computeLateFee(row, parsed, draftDate) {
    const f = row.fields || {};
    const fee = Number(String(f.lateFeeAmount || '').replace(/[^\d.]/g, ''));
    const after = Number(f.lateFeeAfterDay);
    if (!fee || !after) return 0;
    if ((parsed.payments || []).length > 0) return 0;
    if (!draftDate) return 0;
    const day = new Date(`${draftDate}T00:00`).getUTCDate();
    return day > after ? fee : 0;
}

/**
 * Replace the day component of an iso date with the auto-pay day so the
 * recorded payment lands on the actual debit day, not "today".
 */
function deriveAutoPayDate(iso, day) {
    if (!iso || !day) return iso;
    try {
        const d = new Date(`${iso}T00:00`);
        d.setUTCDate(Number(day));
        const m = String(d.getUTCMonth() + 1).padStart(2, '0');
        const dd = String(d.getUTCDate()).padStart(2, '0');
        return `${d.getUTCFullYear()}-${m}-${dd}`;
    } catch (e) {
        return iso;
    }
}

function formatDateShort(iso) {
    if (!iso) return '';
    try {
        const d = new Date(`${iso}T00:00`);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch (e) {
        return iso;
    }
}
function formatDateLong(iso) {
    if (!iso) return '—';
    try {
        const d = new Date(`${iso}T00:00`);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (e) {
        return iso;
    }
}

export default PaymentPopup;
